/**
 * Stranded inventory tracking (2026-08-31) — panel on DashboardPage's
 * owner/operator home. Design: docs/plans/2026-08-31-stranded-inventory-tracking.md.
 *
 * Two read-only conditions surfaced from GET /api/inventory/stranded:
 * unlisted lots sitting on idle stock, and PO lines billed by the vendor
 * but never received. v1 is visibility only — no mutation affordances here.
 *
 * Zero-state is absence: the dashboard is an attention ledger, not a
 * report, so this renders nothing when both lists are empty (mirrors the
 * "Needs you" ledger's own all-clear convention, just one level further:
 * no card at all rather than an all-clear message). Days idle/outstanding
 * are rendered verbatim from the payload — never recomputed from dates
 * here, so the panel and the sentinel check can't drift on threshold math.
 */
import { money } from '../../lib/price-calibration';
import type { StrandedInventoryResponse } from '@sourcito/schemas';

export interface StrandedInventoryPanelProps {
  data: StrandedInventoryResponse;
}

export function StrandedInventoryPanel({ data }: StrandedInventoryPanelProps) {
  const { unlistedLots, unreceivedLines, totals } = data;
  const hasUnlisted = unlistedLots.length > 0;
  const hasUnreceived = unreceivedLines.length > 0;
  if (!hasUnlisted && !hasUnreceived) return null;

  return (
    <section aria-label="Stranded inventory" className="rise mt-4" data-panel="stranded-inventory">
      <div className="bg-surface border border-border-subtle rounded-xl p-5">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-medium text-fg-primary">Stranded inventory</h3>
          <p className="text-xs text-fg-tertiary tabular-nums">
            {hasUnlisted && `${money(totals.unlistedValueCents)} unlisted`}
            {hasUnlisted && hasUnreceived && ' · '}
            {hasUnreceived && `${money(totals.unreceivedValueCents)} never received`}
          </p>
        </div>
        <p className="text-xs text-fg-tertiary mt-1 leading-relaxed">
          Paid-for inventory that is invisible to the selling flow.
        </p>

        {hasUnlisted && (
          <div className="mt-4" data-table="unlisted-lots">
            <p className="text-[11px] uppercase tracking-[0.14em] text-fg-tertiary mb-2">
              Unlisted lots — {unlistedLots.length} product{unlistedLots.length === 1 ? '' : 's'} ·{' '}
              {totals.unlistedUnits.toLocaleString('en-US')} units ·{' '}
              {money(totals.unlistedValueCents)}
            </p>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-fg-tertiary">
                  <th className="font-normal pb-1.5">Product</th>
                  <th className="font-normal pb-1.5 text-right">Units</th>
                  <th className="font-normal pb-1.5 text-right">Value</th>
                  <th className="font-normal pb-1.5 text-right">Days idle</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {/* One product can hold several stranded lots (reordered,
                    received twice) — itemNo alone would collide. */}
                {unlistedLots.map((lot) => (
                  <tr key={`${lot.itemNo}:${lot.receivedAt}`}>
                    <td className="py-1.5 pr-2">
                      <p className="text-fg-primary">{lot.description}</p>
                      <p className="font-mono text-[10px] text-fg-tertiary">{lot.itemNo}</p>
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {lot.unitsIdle.toLocaleString('en-US')}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">{money(lot.valueCents)}</td>
                    <td className="py-1.5 text-right tabular-nums">{lot.daysIdle} days</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {hasUnreceived && (
          <div className={hasUnlisted ? 'mt-5' : 'mt-4'} data-table="unreceived-lines">
            <p className="text-[11px] uppercase tracking-[0.14em] text-fg-tertiary mb-2">
              Never received — {unreceivedLines.length} product
              {unreceivedLines.length === 1 ? '' : 's'} ·{' '}
              {totals.unreceivedUnits.toLocaleString('en-US')} units ·{' '}
              {money(totals.unreceivedValueCents)}
            </p>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-fg-tertiary">
                  <th className="font-normal pb-1.5">Product</th>
                  <th className="font-normal pb-1.5 text-right">Units</th>
                  <th className="font-normal pb-1.5 text-right">Value</th>
                  <th className="font-normal pb-1.5 text-right">Days outstanding</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {/* The same item can be outstanding on two POs. */}
                {unreceivedLines.map((line) => (
                  <tr key={`${line.poExternalId ?? 'na'}:${line.itemNo}`}>
                    <td className="py-1.5 pr-2">
                      <p className="text-fg-primary">{line.description}</p>
                      <p className="font-mono text-[10px] text-fg-tertiary">
                        {line.itemNo}
                        {line.poExternalId ? ` · PO ${line.poExternalId}` : ''}
                      </p>
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {line.qty.toLocaleString('en-US')}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">{money(line.valueCents)}</td>
                    <td className="py-1.5 text-right tabular-nums">{line.daysOutstanding} days</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
